import { useEffect, useState } from 'react';

import { ApiError, api } from '../api';
import type { Report } from './UndoBar';
// C-016 (FND-1611): the fold the card's lower half opens and closes through.
import Unfold from './Unfold';

/**
 * The pre-clipboard-API way to copy: put the text in a field, select it, let the
 * browser copy the selection. Deprecated but not replaced anywhere it matters —
 * it is the only path left on the clients that block navigator.clipboard.
 *
 * The field has to be in the document and visible enough to take a selection, so
 * it is parked off-screen rather than hidden with display:none. readOnly keeps
 * the on-screen keyboard from popping up for the split second it exists.
 */
function copyThroughSelection(text: string): boolean {
  const field = document.createElement('textarea');
  field.value = text;
  field.readOnly = true;
  field.style.position = 'fixed';
  field.style.top = '-1000px';
  field.style.opacity = '0';
  document.body.appendChild(field);
  try {
    field.select();
    field.setSelectionRange(0, text.length);
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    field.remove();
  }
}

/** The middle of a long URL is the part nobody reads; the ends are the part they check. */
function shortLink(link: string): string {
  const withoutScheme = link.replace(/^https?:\/\//, '');
  return withoutScheme.length <= 34 ? withoutScheme : `${withoutScheme.slice(0, 33)}…`;
}

/**
 * The link that lets somebody into the choir, and the way to revoke it.
 *
 * It used to live on the rehearsal panel, next to the code, because that was the
 * only screen the regent had. It belongs with the roster instead: both are about
 * who is in the choir rather than about the rehearsal that is running.
 *
 * Fetched on the way in rather than behind a button. It was a button because the
 * panel it stood on was about a rehearsal; on the choir's own screen the link is
 * the answer to the question the screen exists for, and a tap to reveal it is a
 * tap to be told what the regent came for.
 */
export default function InviteCard({ onDone }: { onDone: Report }) {
  const [invite, setInvite] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Kept apart from a polling channel on purpose: on the panel this state used
  // to be wiped every two seconds, and the copy result blinked out before it
  // could be read.
  const [copyState, setCopyState] = useState<'idle' | 'done' | 'failed'>('idle');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    async function load(): Promise<void> {
      try {
        setInvite((await api.inviteLink()).link);
      } catch (error) {
        setFailure(error instanceof ApiError ? error.message : 'Не удалось получить ссылку');
      }
    }
    void load();
  }, []);

  // Success is a confirmation, not a state: it says the tap landed and then gets
  // out of the way. A failure stays until the next attempt — it asks the regent
  // to do something, so it has to still be there when they look.
  useEffect(() => {
    if (copyState !== 'done') return;
    const timer = window.setTimeout(() => setCopyState('idle'), 2000);
    return () => window.clearTimeout(timer);
  }, [copyState]);

  /**
   * The link is useless where it lies — it has to reach a person in a chat.
   * Picking a long URL out by finger inside a Telegram WebView is fiddly enough
   * that the regent gives up and retypes it, so copying is one tap.
   *
   * Two paths on purpose. navigator.clipboard is the right one, but part of the
   * Telegram clients refuse it: the Android WebView blocks the async API
   * outright, and elsewhere it rejects unless the document is focused. The old
   * execCommand path still works in exactly those places, so it stays as the
   * fallback rather than leaving the regent with a dead button.
   */
  async function copyInvite(): Promise<void> {
    if (invite === null) return;
    try {
      await navigator.clipboard.writeText(invite);
      setCopyState('done');
      return;
    } catch {
      // Fall through to the older path below.
    }
    setCopyState(copyThroughSelection(invite) ? 'done' : 'failed');
  }

  /**
   * Changed on the tap, with five seconds to change it back.
   *
   * It used to ask first, on the grounds that losing the old link to a stray
   * tap is not acceptable. The rule of 2026-08-21 answers that better: the
   * question was put before the regent could see anything, and reading «старая
   * ссылка больше не работает» while looking at the new one is worth more than
   * guessing at it beforehand. What it cost is the server having to keep the
   * previous code — see POST /choir/invite/restore.
   */
  async function rotateInvite(): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      setInvite((await api.rotateInvite()).link);
      // A fresh link is a different link: the old result must not stay up.
      setCopyState('idle');
      onDone('Новая ссылка, прежняя больше не работает', () => void undoRotate());
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'Не удалось сменить ссылку');
    } finally {
      setBusy(false);
    }
  }

  async function undoRotate(): Promise<void> {
    setBusy(true);
    try {
      setInvite((await api.restoreInvite()).link);
      setCopyState('idle');
      setFailure(null);
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'Не удалось вернуть прежнюю ссылку');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <div className="card-row">
        {/* No icon before the label. The frames put a link glyph here and the
            system's own rule refuses it: an icon that repeats its label carries
            nothing. It also cost 56px of a row where «Ссылка-приглашение» needs
            151 and had 118 — so the label was truncated to «Ссылка-пригл…» to
            make room for a picture of itself. */}
        <button className="invite-tap" onClick={() => setExpanded(!expanded)}>
          <span className="tile-title">Ссылка-приглашение</span>
          <span className="tile-sub">{invite === null ? 'Загружаю…' : shortLink(invite)}</span>
        </button>
        <button
          className="btn btn--quiet btn--inline"
          disabled={invite === null}
          onClick={() => void copyInvite()}
        >
          {copyState === 'done' ? 'Скопирована' : 'Копировать'}
        </button>
      </div>

      {/* C-016 (FND-1611): the URL, «Сменить ссылку» and the hint open and close under
          the tap through `Unfold`, as every other fold in the app, instead of cutting in
          and out. `.invite-fold` keeps the card's 10px between the four. */}
      <Unfold open={expanded && invite !== null}>
        <div className="invite-fold">
          {/* The whole URL, selectable in one tap: on a client that refuses the
              clipboard the manual way has to stay possible. */}
          <p className="invite">{invite}</p>
          {copyState === 'failed' && (
            <p className="hint">
              Не удалось скопировать — нажми на ссылку, она выделится целиком, и скопируй
              вручную.
            </p>
          )}
          <button className="btn btn--quiet" disabled={busy} onClick={() => void rotateInvite()}>
            Сменить ссылку
          </button>
          {/* The sentence the question used to carry, and it stands here all the
              time rather than only once somebody has reached for the button:
              who is affected by a new link is worth knowing before the tap, and
              it is the part of the old question that was not about being sure. */}
          <p className="hint">
            Кто уже в хоре — останутся. Всем, кто ещё не вступил, нужно будет разослать новую.
          </p>
        </div>
      </Unfold>

      {failure !== null && <p className="failure">{failure}</p>}
    </section>
  );
}
